import { GetStaticProps, InferGetStaticPropsType } from 'next'
import { Template } from '@/components/common/Template'
import { readFileSync } from 'fs'
import { join } from 'path'
import { css } from '@emotion/react'
import { Divider, colors, mq } from 'plume-ui'
import Link from 'next/link'
import { TouMarkdown } from '@/components/pages/tou/TouMarkdown'
import { EmptyOffsetAnchor } from '@/components/pages/tou/EmptyOffsetAnchor'
import { DefaultHead } from '@/components/common/Head'

type MarkdownProps = {
  ja_enterprise: string
  en_enterprise: string
  ja_circle: string
  en_circle: string
}

export const getStaticProps: GetStaticProps<{
  markdown: MarkdownProps
}> = async () => {
  const ja_enterprise = readFileSync(
    join(process.cwd(), 'public/static/tou/japanese-enterprise.md'),
    'utf8'
  )
  const en_enterprise = readFileSync(
    join(process.cwd(), 'public/static/tou/english-enterprise.md'),
    'utf8'
  )

  const ja_circle = readFileSync(
    join(process.cwd(), 'public/static/tou/japanese-circle.md'),
    'utf8'
  )
  const en_circle = readFileSync(
    join(process.cwd(), 'public/static/tou/english-circle.md'),
    'utf8'
  )

  return {
    props: { markdown: { ja_enterprise, en_enterprise, ja_circle, en_circle } },
  }
}

function EnterpriseTouPage({
  markdown,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  return (
    <Template>
      <DefaultHead title="利用規約 - スピーダ スタートアップ情報リサーチ" />
      <div
        css={css`
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 40px 40px 24px;
          background: ${colors.gray30};
          height: 200px;
          ${mq.untilMedium} {
            height: 128px;
            padding: 24px 24px 16px;
          }
        `}
      >
        <div>
          <h1
            css={css`
              font-size: 42px;
              text-align: center;
              line-height: 1.5;
              margin: 0 0 8px 0;
              letter-spacing: 0.01em;
              ${mq.untilMedium} {
                font-size: 24px;
              }
            `}
            data-testid="tou-title"
          >
            利用規約
          </h1>
          <div
            className="pl-tab-group"
            css={css`
              justify-content: center;
              gap: 8px;
            `}
          >
            <Link href="/tou/guest" passHref>
              <a className="pl-tab" data-testid="guest-tou-link">
                無料版
              </a>
            </Link>
            <a className="pl-tab is-active">
              スピーダ スタートアップ情報リサーチ
            </a>
          </div>
        </div>
      </div>
      <div
        css={css`
          display: flex;
          justify-content: center;
        `}
      >
        <div
          css={css`
            padding: 64px 0 128px 0;
            max-width: 928px;
            ${mq.untilMedium} {
              padding: 40px 24px;
            }
          `}
        >
          <div>
            <EmptyOffsetAnchor id="initial-enterprise-ja">
              <TouMarkdown
                css={css`
                  margin-bottom: 128px;
                  ${mq.untilMedium} {
                    margin-bottom: 80px;
                  }
                `}
              >
                {markdown.ja_enterprise}
              </TouMarkdown>
            </EmptyOffsetAnchor>
            <EmptyOffsetAnchor id="initial-circle-ja">
              <TouMarkdown>{markdown.ja_circle}</TouMarkdown>
            </EmptyOffsetAnchor>
          </div>
          <Divider
            css={css`
              margin: 64px 0;
              ${mq.untilMedium} {
                margin: 40px 0;
              }
            `}
          />
          <div>
            <EmptyOffsetAnchor id="initial-enterprise-en">
              <TouMarkdown
                css={css`
                  margin-bottom: 128px;
                  ${mq.untilMedium} {
                    margin-bottom: 80px;
                  }
                `}
              >
                {markdown.en_enterprise}
              </TouMarkdown>
            </EmptyOffsetAnchor>
            <EmptyOffsetAnchor id="initial-circle-en">
              <TouMarkdown>{markdown.en_circle}</TouMarkdown>
            </EmptyOffsetAnchor>
          </div>
        </div>
      </div>
    </Template>
  )
}

export default EnterpriseTouPage
